// AUTO-GENERATED lucide-react -> Hugeicons compatibility shim.
// Existing `<Eye size={16} className="..."/>`-style usages keep working, but now
// render Hugeicons. Regenerate via scratchpad/gen-shim.js if the icon set changes.
import { forwardRef, type ComponentType } from 'react';
import { HugeiconsIcon, type HugeiconsIconProps, type IconSvgElement } from '@hugeicons/react';
import {
  Alert02Icon,
  AlertCircleIcon,
  AnalyticsUpIcon,
  ArrowDataTransferHorizontalIcon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowLeftDoubleIcon,
  ArrowReloadHorizontalIcon,
  ArrowRight01Icon,
  ArrowRight02Icon,
  ArrowRightDoubleIcon,
  ArrowTurnBackwardIcon,
  Award01Icon,
  BarcodeScanIcon,
  BotIcon,
  Building02Icon,
  Calendar02Icon,
  Cancel01Icon,
  CancelCircleIcon,
  ChartColumnIcon,
  CheckmarkCircle02Icon,
  CircleIcon,
  ClipboardIcon,
  Clock01Icon,
  Clock04Icon,
  CloudDownloadIcon,
  Coins01Icon,
  Comment01Icon,
  Copy01Icon,
  CreditCardIcon,
  Csv01Icon,
  Delete02Icon,
  Dollar01Icon,
  Door01Icon,
  Download01Icon,
  Edit02Icon,
  File01Icon,
  FileExportIcon,
  Files01Icon,
  FlashIcon,
  FloppyDiskIcon,
  GridViewIcon,
  HourglassIcon,
  Idea01Icon,
  Image01Icon,
  ImageAdd01Icon,
  ImageNotFound01Icon,
  InformationCircleIcon,
  Layers01Icon,
  Link01Icon,
  Location01Icon,
  MagicWand01Icon,
  Mail01Icon,
  Menu01Icon,
  MinusSignCircleIcon,
  Moon02Icon,
  Notification01Icon,
  Notification02Icon,
  NotificationOff01Icon,
  Package01Icon,
  Package02Icon,
  PackageReceiveIcon,
  PackageRemove01Icon,
  PaintBoardIcon,
  PartyIcon,
  PencilEdit01Icon,
  PercentIcon,
  PlugSocketIcon,
  PlusSignCircleIcon,
  PlusSignIcon,
  PrinterIcon,
  QrCodeIcon,
  QuoteDownIcon,
  ReceiptIndianRupeeIcon,
  RefreshIcon,
  RulerIcon,
  RupeeIcon,
  Search01Icon,
  SentIcon,
  Settings01Icon,
  Shield01Icon,
  ShoppingBag01Icon,
  ShoppingCart01Icon,
  SmartPhone01Icon,
  SourceCodeIcon,
  SquareIcon,
  SquareLock01Icon,
  StarIcon,
  Store01Icon,
  Sun03Icon,
  Table01Icon,
  Tag01Icon,
  TagsIcon,
  TaskDone01Icon,
  TaskEdit01Icon,
  Tick02Icon,
  Timer01Icon,
  TruckIcon,
  Upload01Icon,
  UserCheck01Icon,
  UserIcon,
  UserMultiple02Icon,
  ViewIcon,
  ViewOffIcon,
  Wallet01Icon,
  WarehouseIcon,
  WavingHand01Icon,
  WhatsappIcon,
} from '@hugeicons/core-free-icons';

type IconProps = Omit<HugeiconsIconProps, 'icon'>;
/** Drop-in replacement for lucide-react's LucideIcon type. */
export type LucideIcon = ComponentType<IconProps>;

function make(icon: IconSvgElement): LucideIcon {
  const C = forwardRef<SVGSVGElement, IconProps>((props, ref) => (
    <HugeiconsIcon ref={ref} icon={icon} strokeWidth={1.8} {...props} />
  ));
  C.displayName = 'Icon';
  return C as unknown as LucideIcon;
}

/**
 * Delete / cancel icons carry the `danger-icon` class so they show red everywhere
 * (styles/index.css); on a solid red button they inherit its white instead.
 */
function makeDanger(icon: IconSvgElement): LucideIcon {
  const C = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
    <HugeiconsIcon ref={ref} icon={icon} strokeWidth={1.8} className={className ? `danger-icon ${className}` : 'danger-icon'} {...props} />
  ));
  C.displayName = 'DangerIcon';
  return C as unknown as LucideIcon;
}

export const AlertCircle = make(AlertCircleIcon);
export const AlertTriangle = make(Alert02Icon);
export const ArrowLeft = make(ArrowLeft01Icon);
export const ArrowLeftRight = make(ArrowDataTransferHorizontalIcon);
export const ArrowRight = make(ArrowRight01Icon);
export const ArrowRightCircle = make(ArrowRight02Icon);
export const Award = make(Award01Icon);
export const Ban = make(Cancel01Icon);
export const BarChart3 = make(ChartColumnIcon);
export const Bell = make(Notification01Icon);
export const BellOff = make(NotificationOff01Icon);
export const BellRing = make(Notification02Icon);
export const Bot = make(BotIcon);
export const Boxes = make(Package02Icon);
export const Building2 = make(Building02Icon);
export const CalendarClock = make(Calendar02Icon);
export const Check = make(Tick02Icon);
export const CheckCircle2 = make(CheckmarkCircle02Icon);
export const ChevronDown = make(ArrowDown01Icon);
export const ChevronLeft = make(ArrowLeft01Icon);
export const ChevronRight = make(ArrowRight01Icon);
export const ChevronsLeft = make(ArrowLeftDoubleIcon);
export const ChevronsRight = make(ArrowRightDoubleIcon);
export const Circle = make(CircleIcon);
export const ClipboardCheck = make(TaskDone01Icon);
export const ClipboardEdit = make(TaskEdit01Icon);
export const ClipboardList = make(ClipboardIcon);
export const Clock = make(Clock01Icon);
export const Code2 = make(SourceCodeIcon);
export const Copy = make(Copy01Icon);
export const CreditCard = make(CreditCardIcon);
export const DollarSign = make(Dollar01Icon);
export const DoorOpen = make(Door01Icon);
export const Download = make(Download01Icon);
export const DownloadCloud = make(CloudDownloadIcon);
export const Edit = make(Edit02Icon);
export const Eye = make(ViewIcon);
export const EyeOff = make(ViewOffIcon);
export const FileDown = make(FileExportIcon);
export const FileQuestion = make(File01Icon);
export const FileSpreadsheet = make(Csv01Icon);
export const FileStack = make(Files01Icon);
export const FileText = make(File01Icon);
export const HandCoins = make(Coins01Icon);
export const HandWave = make(WavingHand01Icon);
export const History = make(Clock04Icon);
export const Hourglass = make(HourglassIcon);
export const ImageIcon = make(Image01Icon);
export const ImageOff = make(ImageNotFound01Icon);
export const ImagePlus = make(ImageAdd01Icon);
export const IndianRupee = make(RupeeIcon);
export const Info = make(InformationCircleIcon);
export const Layers = make(Layers01Icon);
export const LayoutGrid = make(GridViewIcon);
export const Lightbulb = make(Idea01Icon);
export const Link2 = make(Link01Icon);
export const Lock = make(SquareLock01Icon);
export const Mail = make(Mail01Icon);
export const MapPin = make(Location01Icon);
export const Menu = make(Menu01Icon);
export const MessageSquarePlus = make(Comment01Icon);
export const MessageSquareQuote = make(QuoteDownIcon);
export const MinusCircle = make(MinusSignCircleIcon);
export const Moon = make(Moon02Icon);
export const Package = make(Package01Icon);
export const PackageCheck = make(PackageReceiveIcon);
export const PackageMinus = make(PackageRemove01Icon);
export const PackageX = make(PackageRemove01Icon);
export const Palette = make(PaintBoardIcon);
export const PartyPopper = make(PartyIcon);
export const Pencil = make(PencilEdit01Icon);
export const Percent = make(PercentIcon);
export const Plug = make(PlugSocketIcon);
export const Plus = make(PlusSignIcon);
export const PlusCircle = make(PlusSignCircleIcon);
export const Printer = make(PrinterIcon);
export const Quote = make(QuoteDownIcon);
export const Receipt = make(ReceiptIndianRupeeIcon);
export const RefreshCw = make(RefreshIcon);
export const RotateCcw = make(ArrowReloadHorizontalIcon);
export const Rows3 = make(Menu01Icon);
export const Ruler = make(RulerIcon);
export const Save = make(FloppyDiskIcon);
export const ScanBarcode = make(BarcodeScanIcon);
export const ScanLine = make(QrCodeIcon);
export const Search = make(Search01Icon);
export const Send = make(SentIcon);
export const Settings = make(Settings01Icon);
export const ShieldAlert = make(Shield01Icon);
export const ShoppingBag = make(ShoppingBag01Icon);
export const ShoppingCart = make(ShoppingCart01Icon);
export const Smartphone = make(SmartPhone01Icon);
export const Square = make(SquareIcon);
export const Star = make(StarIcon);
export const Store = make(Store01Icon);
export const Sun = make(Sun03Icon);
export const Table = make(Table01Icon);
export const Tag = make(Tag01Icon);
export const Tags = make(TagsIcon);
export const Timer = make(Timer01Icon);
export const Trash2 = makeDanger(Delete02Icon);
export const TrendingUp = make(AnalyticsUpIcon);
export const Truck = make(TruckIcon);
export const Undo2 = make(ArrowTurnBackwardIcon);
export const Upload = make(Upload01Icon);
export const User = make(UserIcon);
export const UserCheck = make(UserCheck01Icon);
export const Users = make(UserMultiple02Icon);
export const Wallet = make(Wallet01Icon);
export const Wand2 = make(MagicWand01Icon);
export const Warehouse = make(WarehouseIcon);
export const Whatsapp = make(WhatsappIcon);
export const X = make(Cancel01Icon);
export const XCircle = makeDanger(CancelCircleIcon);
export const Zap = make(FlashIcon);
